import { ILaya } from "../../ILaya";
import { Const } from "../Const";
import { RenderState } from "../RenderDriver/RenderModuleData/Design/RenderState";
import { Shader3D } from "../RenderEngine/RenderShader/Shader3D";
import { SpriteConst } from "../display/SpriteConst";
import { Event } from "../events/Event";
import { Matrix } from "../maths/Matrix";
import { Matrix4x4 } from "../maths/Matrix4x4";
import { Vector2 } from "../maths/Vector2";
import { Stat } from "../utils/Stat";
import { BlendMode } from "../webgl/canvas/BlendMode";
import { TextTexture } from "../webgl/text/TextTexture";
import { DefferTouchResContext } from "./DefferTouchResContext";
import { RenderToCache } from "./RenderToCache";
export class Cache_Info {
    constructor() {
        this.page = null;
    }
    reset() {
        this.page && this.page.reset();
        this.page = null;
    }
}
function mergeClipMatrix(a, b, out) {
    let amaxx = a.tx + a.a;
    let amaxy = a.ty + a.d;
    let bmaxx = b.tx + b.a;
    let bmaxy = b.ty + b.d;
    let minx = out.tx = Math.max(a.tx, b.tx);
    let miny = out.ty = Math.max(a.ty, b.ty);
    out.b = out.c = 0;
    out.tx = minx;
    out.ty = miny;
    if (amaxx <= b.tx || amaxy <= b.ty || bmaxx <= a.tx || bmaxy <= a.ty) {
        out.a = -0.1;
        out.d = -0.1;
    }
    else {
        let maxx = Math.min(amaxx, bmaxx);
        let maxy = Math.min(amaxy, bmaxy);
        out.a = maxx - minx;
        out.d = maxy - miny;
    }
    return out;
}
class RenderPageContext {
    constructor(ctx, x, y) {
        this.alpha = 1;
        this.width = 0;
        this.height = 0;
        this.blend = 0;
        let mat = this.curMatrix = ctx._curMat.clone();
        mat.tx += mat.a * x + mat.c * y;
        mat.ty += mat.b * x + mat.d * y;
        this.alpha = ctx.globalAlpha;
        this.render2d = ctx.render2D;
        this.width = ctx.width;
        this.height = ctx.height;
        this.clipInfo = ctx._globalClipMatrix.clone();
        this.blend = ctx._nBlendType;
    }
    _copyClipInfo(shaderValue) {
        let clipInfo = this.clipInfo;
        var cm = shaderValue.clipMatDir;
        cm.x = clipInfo.a;
        cm.y = clipInfo.b;
        cm.z = clipInfo.c;
        cm.w = clipInfo.d;
        shaderValue.clipMatDir = cm;
        var cmp = shaderValue.clipMatPos;
        cmp.x = clipInfo.tx;
        cmp.y = clipInfo.ty;
        shaderValue.clipMatPos = cmp;
    }
    clipRect(rect) {
        let x = rect.x;
        let y = rect.y;
        let width = rect.width;
        let height = rect.height;
        var cm = this.clipInfo;
        var minx = cm.tx;
        var miny = cm.ty;
        var maxx = minx + cm.a;
        var maxy = miny + cm.d;
        if (width >= Const.MAX_CLIP_SIZE) {
            cm.a = cm.d = Const.MAX_CLIP_SIZE;
            cm.b = cm.c = cm.tx = cm.ty = 0;
        }
        else {
            let mat = this.curMatrix;
            if (mat._bTransform) {
                cm.tx = x * mat.a + y * mat.c + mat.tx;
                cm.ty = x * mat.b + y * mat.d + mat.ty;
                cm.a = width * mat.a;
                cm.b = width * mat.b;
                cm.c = height * mat.c;
                cm.d = height * mat.d;
            }
            else {
                cm.tx = x + mat.tx;
                cm.ty = y + mat.ty;
                cm.a = width;
                cm.b = cm.c = 0;
                cm.d = height;
            }
        }
        if (cm.a > 0 && cm.d > 0) {
            var cmaxx = cm.tx + cm.a;
            var cmaxy = cm.ty + cm.d;
            if (cmaxx <= minx || cmaxy <= miny || cm.tx >= maxx || cm.ty >= maxy) {
                cm.a = -0.1;
                cm.d = -0.1;
            }
            else {
                if (cm.tx < minx) {
                    cm.a -= (minx - cm.tx);
                    cm.tx = minx;
                }
                if (cmaxx > maxx) {
                    cm.a -= (cmaxx - maxx);
                }
                if (cm.ty < miny) {
                    cm.d -= (miny - cm.ty);
                    cm.ty = miny;
                }
                if (cmaxy > maxy) {
                    cm.d -= (cmaxy - maxy);
                }
                if (cm.a <= 0)
                    cm.a = -0.1;
                if (cm.d <= 0)
                    cm.d = -0.1;
            }
        }
    }
    setBlendMode(blend) {
        this.blend = BlendMode.TOINT[blend];
    }
    _applyBlend(shaderValue) {
        let shaderdata = shaderValue.shaderData;
        switch (this.blend) {
            case 1:
            case 3:
            case 5:
                shaderdata.setInt(Shader3D.BLEND_SRC, RenderState.BLENDPARAM_ONE);
                shaderdata.setInt(Shader3D.BLEND_DST, RenderState.BLENDPARAM_ONE);
                break;
            case 2:
                shaderdata.setInt(Shader3D.BLEND_SRC, RenderState.BLENDPARAM_DST_COLOR);
                shaderdata.setInt(Shader3D.BLEND_DST, RenderState.BLENDPARAM_ONE_MINUS_SRC_ALPHA);
                break;
            case 6:
                shaderdata.setInt(Shader3D.BLEND_SRC, RenderState.BLENDPARAM_ZERO);
                shaderdata.setInt(Shader3D.BLEND_DST, RenderState.BLENDPARAM_SRC_ALPHA);
                break;
            case 7:
                shaderdata.setInt(Shader3D.BLEND_SRC, RenderState.BLENDPARAM_ZERO);
                shaderdata.setInt(Shader3D.BLEND_DST, RenderState.BLENDPARAM_ZERO);
                break;
            case 9:
                shaderdata.setInt(Shader3D.BLEND_SRC, RenderState.BLENDPARAM_SRC_ALPHA);
                shaderdata.setInt(Shader3D.BLEND_DST, RenderState.BLENDPARAM_ONE_MINUS_SRC_ALPHA);
                break;
            default:
                shaderdata.setInt(Shader3D.BLEND_SRC, RenderState.BLENDPARAM_ONE);
                shaderdata.setInt(Shader3D.BLEND_DST, RenderState.BLENDPARAM_ONE_MINUS_SRC_ALPHA);
        }
    }
}
export class CachePage {
    constructor() {
        this.sprite = null;
        this.meshes = null;
        this.defferTouchRes = null;
        this.defferTouchResRand = null;
        this.children = null;
    }
    reset() {
        this.clearGPUObject();
    }
    clearGPUObject() {
        if (this.meshes) {
            this.meshes.forEach(m => {
                m.destroyGPUResource();
            });
        }
    }
    render(sprite, context, isRoot) {
        let spriteTrans = sprite.transform;
        let curMat = context.curMatrix;
        if (isRoot) {
            curMat.copyTo(worldMat);
        }
        else {
            let x = sprite._x;
            let y = sprite._y;
            if (spriteTrans) {
                spriteTrans.copyTo(tmpMat);
                tmpMat.tx = x;
                tmpMat.ty = y;
                Matrix.mul(tmpMat, curMat, worldMat);
            }
            else {
                worldMat.a = curMat.a;
                worldMat.b = curMat.b;
                worldMat.c = curMat.c;
                worldMat.d = curMat.d;
                worldMat.tx = curMat.a * x + curMat.c * y + curMat.tx;
                worldMat.ty = curMat.b * x + curMat.d * y + curMat.ty;
            }
            context.alpha *= sprite.alpha;
            if (sprite.blendMode) {
                context.setBlendMode(sprite.blendMode);
            }
        }
        vec21.setValue(context.width, context.height);
        let wMat4 = tmpMat4;
        let mate = wMat4.elements;
        mate[0] = worldMat.a;
        mate[1] = worldMat.b;
        mate[4] = worldMat.c;
        mate[5] = worldMat.d;
        mate[12] = worldMat.tx;
        mate[13] = worldMat.ty;
        this.meshes.forEach(renderinfo => {
            let render = context.render2d;
            let curMtl = renderinfo.mtl;
            if (curMtl.textureHost instanceof TextTexture) {
                curMtl.textureHost.touchTexture();
            }
            curMtl.size = vec21;
            let clipMat = renderinfo.localClipMatrix;
            if (clipMat.a == Const.MAX_CLIP_SIZE && clipMat.d == Const.MAX_CLIP_SIZE) {
                context.clipInfo.copyTo(tmpMat1);
            }
            else {
                Matrix.mul(clipMat, worldMat, tmpMat1);
                mergeClipMatrix(context.clipInfo, tmpMat1, tmpMat1);
            }
            let clipDir = curMtl.clipMatDir;
            let clipPos = curMtl.clipMatPos;
            clipDir.x = tmpMat1.a;
            clipDir.y = tmpMat1.b;
            clipDir.z = tmpMat1.c;
            clipDir.w = tmpMat1.d;
            curMtl.clipMatDir = clipDir;
            clipPos.x = tmpMat1.tx;
            clipPos.y = tmpMat1.ty;
            curMtl.clipMatPos = clipPos;
            curMtl.mmat = wMat4;
            curMtl.vertAlpha = context.alpha;
            context._applyBlend(curMtl);
            render.drawElement(renderinfo.renderElement);
        });
        this.defferTouchRes.forEach(res => { res.touch(); });
        this.defferTouchResRand.forEach(res => res.touch());
        this.children && this.children.forEach(sp => {
            let oldMat = context.curMatrix.clone();
            let oldAlpha = context.alpha;
            let parentCacheInfo = sp._parent._cacheStyle.cacheInfo;
            let offmat = parentCacheInfo.mat;
            Matrix.mul(offmat, oldMat, context.curMatrix);
            context.alpha *= parentCacheInfo.alpha;
            let oldBlend = context.blend;
            if (parentCacheInfo.blend != undefined) {
                context.setBlendMode(parentCacheInfo.blend);
            }
            let oldClipMatrix = context.clipInfo.clone();
            let clipMat = parentCacheInfo.clipMatrix;
            Matrix.mul(clipMat, context.curMatrix, tmpMat1);
            mergeClipMatrix(context.clipInfo, tmpMat1, context.clipInfo);
            sp._cacheStyle.cacheInfo.page.render(sp, context, false);
            context.curMatrix = oldMat;
            context.alpha = oldAlpha;
            context.blend = oldBlend;
            context.clipInfo = oldClipMatrix;
        });
    }
}
var vec21 = new Vector2();
export class SpriteCache {
    static curMatSubSpriteMat(sprite, curMat, outMat) {
        if (sprite._renderType & SpriteConst.TRANSFORM) {
            sprite.transform.copyTo(invMat);
            invMat.tx = sprite._x;
            invMat.ty = sprite._y;
            invMat.invert();
            Matrix.mul(invMat, curMat, outMat);
        }
        else {
            curMat.copyTo(outMat);
            let cx = -sprite._x;
            let cy = -sprite._y;
            outMat.tx += (outMat.a * cx + outMat.c * cy);
            outMat.ty += (outMat.b * cx + outMat.d * cy);
        }
        return outMat;
    }
    static renderCacheAsNormal(context, sprite, next, x, y) {
        let rebuild = false;
        var cache = sprite._getCacheStyle().cacheInfo.page;
        if (!cache || sprite._needRepaint() || ILaya.stage.isGlobalRepaint()) {
            if (sprite.alpha <= 1e-6) {
                sprite.alpha = 0.001;
            }
            rebuild = true;
            if (context instanceof DefferTouchResContext) {
                let parentPage = context.cache;
                if (!parentPage.children) {
                    parentPage.children = [sprite];
                }
                else if (parentPage.children.indexOf(sprite) < 0) {
                    parentPage.children.push(sprite);
                }
                let parentNode = sprite._parent;
                let parentCacheInfo = parentNode._cacheStyle.cacheInfo;
                parentCacheInfo.page = parentPage;
                if (context.genID != parentCacheInfo.contextID) {
                    parentCacheInfo.contextID = context.genID;
                    let curMat = context._curMat.clone();
                    if (x != 0 || y != 0) {
                        curMat.tx += x * curMat.a + y * curMat.c;
                        curMat.ty += x * curMat.b + y * curMat.d;
                    }
                    parentCacheInfo.mat = SpriteCache.curMatSubSpriteMat(sprite, curMat, curMat);
                    parentCacheInfo.alpha = context.globalAlpha / sprite.alpha;
                    let pageRootSprite = parentPage.sprite;
                    if (parentNode.blendMode) {
                        parentCacheInfo.blend = parentNode.blendMode;
                    }
                    else {
                        let curNode = parentNode;
                        while (curNode != pageRootSprite) {
                            curNode = curNode._parent;
                            if (curNode.blendMode) {
                                parentCacheInfo.blend = curNode.blendMode;
                                break;
                            }
                        }
                    }
                    parentCacheInfo.clipMatrix = context._globalClipMatrix.clone();
                }
            }
            cache = sprite._cacheStyle.cacheInfo.page = new CachePage();
            cache.sprite = sprite;
            Stat.canvasNormal++;
            let ctx = new DefferTouchResContext();
            ctx.cache = cache;
            let renderer = new RenderToCache();
            ctx.render2D = renderer;
            ctx.startRender();
            next._fun(sprite, ctx, 0, 0);
            ctx.endRender();
            cache.meshes = renderer.renderResult;
            cache.defferTouchRes = ctx.mustTouchRes;
            cache.defferTouchResRand = ctx.randomTouchRes;
            sprite.once(Event.REMOVED, () => {
                cache = sprite._cacheStyle.cacheInfo.page;
                cache.reset();
            });
        }
        if (!(context instanceof DefferTouchResContext)) {
            let ctx = new RenderPageContext(context, x, y);
            cache.render(sprite, ctx, true);
        }
        return rebuild;
    }
}
var worldMat = new Matrix;
var invMat = new Matrix;
var tmpMat = new Matrix;
var tmpMat4 = new Matrix4x4;
var tmpMat1 = new Matrix();
